import React from 'react'

/**
 * Helper to determine icon, badge color, and background style for call logs
 */
function getLogIcon(log) {
  if (log.call_status === 'MISSED' || log.call_status === 'NO_ANSWER') {
    return { icon: 'call_missed', color: 'text-red-600', bg: 'bg-red-50' }
  }
  if (log.direction === 'INBOUND') {
    return { icon: 'call_received', color: 'text-blue-600', bg: 'bg-blue-50' }
  }
  return { icon: 'call_made', color: 'text-emerald-600', bg: 'bg-emerald-50' }
}

/**
 * RecentCallHistoryCard Component
 * Displays recent 24-hour call activity list with status indicators, timestamps, and duration.
 */
export default function RecentCallHistoryCard({
  recentLogs,
  logsLoading,
  onViewFullHistory,
}) {
  return (
    <div className="xl:col-span-7 bg-surface-container-lowest p-5 rounded-2xl border border-outline-variant/25 shadow-xs space-y-4 flex flex-col justify-between">
      <div>
        {/* Header: Title + Link */}
        <div className="flex items-center justify-between border-b border-outline-variant/15 pb-3">
          <div>
            <h2 className="text-sm font-extrabold uppercase tracking-wider font-display text-on-surface">
              Recent Call History
            </h2>
            <p className="text-[11px] text-on-surface-variant font-medium">
              Your activity for the last 24 hours.
            </p>
          </div>
          <button
            type="button"
            onClick={onViewFullHistory}
            className="text-xs font-bold text-primary hover:underline flex items-center gap-1 cursor-pointer"
          >
            <span>View Full History</span>
            <span className="material-symbols-outlined text-sm">arrow_forward</span>
          </button>
        </div>

        {/* Call Log Items List */}
        <div className="space-y-3 pt-3">
          {logsLoading && (
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent mb-2" />
              <span className="text-xs font-semibold text-on-surface-variant">Loading recent calls...</span>
            </div>
          )}
          {!logsLoading && recentLogs.length === 0 && (
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <span className="material-symbols-outlined text-3xl text-outline-variant mb-1">
                history_toggle_off
              </span>
              <p className="text-xs font-bold text-on-surface">No Recent Calls</p>
              <p className="text-[11px] text-on-surface-variant mt-0.5">
                Calls placed or received in the last 24 hours will appear here.
              </p>
            </div>
          )}
          {!logsLoading &&
            recentLogs.map((log) => {
              const { icon, color, bg } = getLogIcon(log)
              const isMissed = log.call_status === 'MISSED' || log.call_status === 'NO_ANSWER'
              const callTime = log.start_time
                ? new Date(log.start_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                : ''
              const durMin = log.duration_seconds
                ? Math.floor(log.duration_seconds / 60).toString().padStart(2, '0')
                : '00'
              const durSec = log.duration_seconds
                ? (log.duration_seconds % 60).toString().padStart(2, '0')
                : '00'

              return (
                <div
                  key={log.id}
                  className="flex items-center justify-between p-3 rounded-xl bg-surface-container-low/50 hover:bg-surface-container-low transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-9 h-9 rounded-full ${bg} flex items-center justify-center ${color} shrink-0`}>
                      <span className="material-symbols-outlined text-base">{icon}</span>
                    </div>
                    <div>
                      <h3 className="text-xs font-bold text-on-surface">
                        {log.caller_name || log.customer_name || log.caller_number}
                      </h3>
                      <p className={`text-[11px] ${isMissed ? 'text-red-600 font-semibold' : 'text-on-surface-variant'}`}>
                        {isMissed ? 'Missed' : log.caller_number}
                      </p>
                    </div>
                  </div>

                  <div className="text-center hidden sm:block">
                    <span className={`text-xs font-bold block ${isMissed ? 'text-red-600' : 'text-on-surface'}`}>
                      {isMissed ? 'Missed' : `${durMin}:${durSec}`}
                    </span>
                    <span className="text-[9px] uppercase tracking-wider font-semibold text-on-surface-variant">
                      {isMissed ? 'Status' : 'Duration'}
                    </span>
                  </div>

                  <div className="text-right">
                    <span className="text-xs font-medium text-on-surface block">{callTime}</span>
                    <span className="text-[10px] text-on-surface-variant">
                      {log.start_time ? new Date(log.start_time).toLocaleDateString([], { day: 'numeric', month: 'short' }) : ''}
                    </span>
                  </div>
                </div>
              )
            })}
        </div>
      </div>
    </div>
  )
}
